import { useEffect, useState, type FormEvent } from "react";
import { Textarea } from "../../components/TextField/Textarea";
import { Button } from "../../components/Button/Button";
import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { Spinner } from "../../components/Spinner/Spinner";
import { IconAlert, IconInfo } from "../../components/icons/icons";
import { listAdminAuditEvents, updateAdminTenantStatus } from "../../lib/api/admin";
import { ApiError } from "../../lib/apiClient";
import { formatEntryDateTime } from "../../lib/format";
import type { AdminAuditEvent, AdminTenant } from "../../types";
import styles from "./TenantGovernance.module.css";

const ACCESS_DENIED_MESSAGE =
  "Access denied for this business. If your access changed recently, sign out and sign in again to refresh it.";

const AUDIT_ACTION_LABEL: Record<AdminAuditEvent["action"], string> = {
  TenantCreated: "Business created",
  TenantSuspended: "Suspended",
  TenantReactivated: "Reactivated",
};

const REASON_MIN = 3;
const REASON_MAX = 240;

type AuditState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; events: AdminAuditEvent[]; nextCursor?: string };

interface TenantGovernanceProps {
  tenant: AdminTenant;
  token: string;
  onUnauthorized: () => void;
  /** Called with the fresh AdminTenant after a successful status change, so the caller can
   * splice it into top-level state without a reload. */
  onTenantUpdated: (tenant: AdminTenant) => void;
}

/** Administrator-only status control for the selected business: suspend or reactivate with a
 * required reason, plus that business's audit history. Rendered as the admin workspace's
 * `headerNotice`, above the shared tab set every role uses. */
export function TenantGovernance({ tenant, token, onUnauthorized, onTenantUpdated }: TenantGovernanceProps) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [actionStatus, setActionStatus] = useState<"idle" | "saving" | "error">("idle");
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [auditState, setAuditState] = useState<AuditState>({ status: "loading" });
  const [auditReloadKey, setAuditReloadKey] = useState(0);
  const [loadingMoreAudit, setLoadingMoreAudit] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const pendingAction: "suspend" | "reactivate" = tenant.status === "active" ? "suspend" : "reactivate";

  // Switching businesses resets the form and re-fetches that business's own history.
  useEffect(() => {
    setReason("");
    setReasonError(undefined);
    setActionStatus("idle");
    setActionMessage(null);
    setSuccessMessage(null);
  }, [tenant.tenantId]);

  useEffect(() => {
    let cancelled = false;
    setAuditState({ status: "loading" });
    setLoadMoreError(null);
    listAdminAuditEvents(token, tenant.tenantId)
      .then(({ events, nextCursor }) => {
        if (!cancelled) setAuditState({ status: "ready", events, nextCursor });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setAuditState({ status: "error", message: ACCESS_DENIED_MESSAGE });
          return;
        }
        setAuditState({
          status: "error",
          message: err instanceof Error ? err.message : "Couldn't load activity. Please try again.",
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.tenantId, token, auditReloadKey]);

  async function handleLoadMoreAudit() {
    if (auditState.status !== "ready" || !auditState.nextCursor) return;
    setLoadingMoreAudit(true);
    setLoadMoreError(null);
    try {
      const page = await listAdminAuditEvents(token, tenant.tenantId, { cursor: auditState.nextCursor });
      setAuditState((prev) =>
        prev.status === "ready"
          ? { status: "ready", events: [...prev.events, ...page.events], nextCursor: page.nextCursor }
          : prev,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setLoadMoreError(ACCESS_DENIED_MESSAGE);
        return;
      }
      setLoadMoreError(err instanceof Error ? err.message : "Couldn't load older activity. Please try again.");
    } finally {
      setLoadingMoreAudit(false);
    }
  }

  function validateReason(): string | undefined {
    const trimmed = reason.trim();
    if (trimmed.length < REASON_MIN || trimmed.length > REASON_MAX) {
      return `Use ${REASON_MIN}–${REASON_MAX} characters.`;
    }
    return undefined;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const error = validateReason();
    setReasonError(error);
    if (error) return;

    setActionStatus("saving");
    setActionMessage(null);
    setSuccessMessage(null);
    try {
      const { tenant: updated } = await updateAdminTenantStatus(token, tenant.tenantId, {
        status: pendingAction === "suspend" ? "suspended" : "active",
        reason: reason.trim(),
      });
      onTenantUpdated(updated);
      setReason("");
      setActionStatus("idle");
      setSuccessMessage(pendingAction === "suspend" ? "Business suspended." : "Business reactivated.");
      setAuditReloadKey((key) => key + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setActionStatus("error");
      setActionMessage(
        err instanceof ApiError && err.status === 403
          ? ACCESS_DENIED_MESSAGE
          : err instanceof Error
            ? err.message
            : "Couldn't update the business status. Please try again.",
      );
    }
  }

  return (
    <section className={styles.panel} aria-label={`${tenant.name} status`}>
      <div className={styles.panelHeader}>
        <div>
          <h2 className={styles.heading}>Business status</h2>
          <p className={styles.subheading}>
            Suspending immediately blocks the owner's member data, analytics, and brand kit controls.
          </p>
        </div>
        <span className={tenant.status === "active" ? styles.statusActive : styles.statusSuspended}>
          {tenant.status === "active" ? "Active" : "Suspended"}
        </span>
      </div>

      {successMessage && (
        <InlineNotice tone="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </InlineNotice>
      )}
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
        {pendingAction === "suspend" ? (
          <p className={styles.consequenceSuspend}>
            <IconAlert className={styles.consequenceIcon} width={18} height={18} />
            Suspending {tenant.name} hides its member data, analytics, and brand kit from the owner and shows
            them a suspended notice until an administrator reactivates the business.
          </p>
        ) : (
          <p className={styles.consequenceReactivate}>
            <IconInfo className={styles.consequenceIcon} width={18} height={18} />
            Reactivating {tenant.name} immediately restores the owner's access to member data, analytics, and
            the brand kit.
          </p>
        )}

        <Textarea
          label="Reason"
          hint="Recorded in this business's activity log below."
          error={reasonError}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (reasonError) setReasonError(undefined);
          }}
          rows={2}
          maxLength={REASON_MAX}
          placeholder={
            pendingAction === "suspend"
              ? "e.g. Billing dispute pending owner response…"
              : "e.g. Dispute resolved, resuming service…"
          }
          disabled={actionStatus === "saving"}
        />

        {actionStatus === "error" && actionMessage && <InlineNotice tone="error">{actionMessage}</InlineNotice>}

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            busy={actionStatus === "saving"}
          >
            {pendingAction === "suspend" ? "Suspend business" : "Reactivate business"}
          </Button>
        </div>
      </form>

      <div className={styles.history}>
        <h3 className={styles.historyHeading}>Activity</h3>

        {auditState.status === "loading" && <Spinner label={`Loading ${tenant.name}'s activity`} />}

        {auditState.status === "error" && (
          <div className={styles.historyError}>
            <InlineNotice tone="error">{auditState.message}</InlineNotice>
            <Button type="button" variant="secondary" onClick={() => setAuditReloadKey((key) => key + 1)}>
              Try again
            </Button>
          </div>
        )}

        {auditState.status === "ready" && auditState.events.length === 0 && (
          <p className={styles.historyEmpty}>No activity recorded yet.</p>
        )}

        {auditState.status === "ready" && auditState.events.length > 0 && (
          <>
            <div
              className={styles.scroller}
              tabIndex={0}
              aria-label={`${tenant.name} activity log; scroll horizontally for more columns`}
            >
              <table className={styles.table}>
                <caption className="visually-hidden">{tenant.name}'s activity log</caption>
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Reason</th>
                    <th scope="col">Administrator</th>
                    <th scope="col">When</th>
                  </tr>
                </thead>
                <tbody>
                  {auditState.events.map((event) => (
                    <tr key={event.auditId}>
                      <td className={styles.eventAction}>{AUDIT_ACTION_LABEL[event.action]}</td>
                      <td>{event.reason}</td>
                      <td>{event.actorUsername}</td>
                      <td className="tabular-nums" title={formatEntryDateTime(event.occurredAt)}>
                        {formatEntryDateTime(event.occurredAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {loadMoreError && <InlineNotice tone="error">{loadMoreError}</InlineNotice>}
            {auditState.nextCursor && (
              <Button
                type="button"
                variant="secondary"
                busy={loadingMoreAudit}
                onClick={() => void handleLoadMoreAudit()}
              >
                Show older activity
              </Button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
